import { ConsentMethod } from '@chiroup/core/enums/ConsentMethod.enum';
import { SmsConsentStatus } from '@chiroup/core/enums/SmsConsentStatus.enum';
import { getConsentMetadata } from '@chiroup/core/functions/consentUtils';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import React, { useState } from 'react';
import { CarrierOptedOutWarning } from '../consent/CarrierOptedOutWarning';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from './Checkbox';

type TextingConsentProps = {
  patientId?: string;
  transactionalConsent?: boolean;
  marketingConsent?: boolean;
  transactionalConsentStatus?: SmsConsentStatus;
  marketingConsentStatus?: SmsConsentStatus;
  initialTransactionalConsent?: boolean;
  initialMarketingConsent?: boolean;
  onTransactionalChange?: (value: boolean, metadata?: any) => void;
  onMarketingChange?: (value: boolean, metadata?: any) => void;
  transactionalErrors?: FormError;
  marketingErrors?: FormError;
  disabled?: boolean;
  className?: string;
  termsUrl?: string;
  privacyUrl?: string;
  consentMethod?: ConsentMethod;
  captureMetadata?: boolean;
  clinicName?: string;
  hideIfOptedIn?: boolean;
  showConsentScript?: boolean;
  hasEHR?: boolean;
  patientView?: boolean;
  hideHeader?: boolean;
  clinicPath?: string;
  hideMarketingConsent?: boolean; // Hide marketing consent section for non-US or non-messaging clinics
  clinicId: number;
  disclosesAllUseCases?: boolean; // Enumerate all 10DLC campaign use cases in consent copy (optin.healthcom.io)
};

export const TextingConsentCheckboxes: React.FC<TextingConsentProps> = ({
  patientId,
  transactionalConsent = false,
  marketingConsent = false,
  transactionalConsentStatus,
  marketingConsentStatus,
  initialTransactionalConsent,
  initialMarketingConsent,
  onTransactionalChange,
  onMarketingChange,
  transactionalErrors,
  marketingErrors,
  disabled = false,
  className = '',
  termsUrl = '/terms-and-conditions',
  privacyUrl = '/privacy-policy',
  consentMethod = ConsentMethod.WEB_FORM,
  captureMetadata = true,
  clinicName = 'the clinic',
  hideIfOptedIn = false,
  showConsentScript = false,
  hasEHR = false,
  patientView = false,
  hideHeader = false,
  clinicPath,
  hideMarketingConsent = false,
  clinicId,
  disclosesAllUseCases = false,
}) => {
  // Track if user has interacted with each checkbox separately
  const [hasInteractedWithTransactional, setHasInteractedWithTransactional] = useState(false);
  const [hasInteractedWithMarketing, setHasInteractedWithMarketing] = useState(false);

  const handleTransactionalChange = (value: boolean) => {
    if (!onTransactionalChange) return;

    // Mark that user has interacted with transactional checkbox
    setHasInteractedWithTransactional(true);

    if (captureMetadata) {
      const metadata = getConsentMetadata(consentMethod);
      onTransactionalChange(value, { patientId, ...metadata });
    } else {
      onTransactionalChange(value);
    }
  };

  const handleMarketingChange = (value: boolean) => {
    if (!onMarketingChange) return;

    // Mark that user has interacted with marketing checkbox
    setHasInteractedWithMarketing(true);

    if (captureMetadata) {
      const metadata = getConsentMetadata(ConsentMethod.DIGITAL_SIGNATURE); // Marketing always uses digital signature
      onMarketingChange(value, { patientId, requiresDoubleOptIn: true, ...metadata });
    } else {
      onMarketingChange(value);
    }
  };

  // Hide checkboxes if already opted in and hideIfOptedIn is true (only for patient view)
  // Use initial values if provided, otherwise fall back to current values
  const initialTransactional = initialTransactionalConsent ?? transactionalConsent;
  const initialMarketing = initialMarketingConsent ?? marketingConsent;

  // Check if patient has explicit opt-in (not implied consent) - use INITIAL status values
  const hasInitialExplicitTransactionalOptIn =
    transactionalConsentStatus === SmsConsentStatus.OPTED_IN;
  const hasInitialExplicitMarketingOptIn = marketingConsentStatus === SmsConsentStatus.OPTED_IN;

  // If hideIfOptedIn is true and both were initially opted in, and user hasn't interacted with either, hide entire component
  if (
    hideIfOptedIn &&
    !hasInteractedWithTransactional &&
    !hasInteractedWithMarketing &&
    hasInitialExplicitTransactionalOptIn &&
    hasInitialExplicitMarketingOptIn
  ) {
    return null;
  }

  // For patient view, determine which checkboxes to show based on initial state and user interaction
  // Only show a checkbox if:
  // 1. User has interacted with THAT specific checkbox, OR
  // 2. hideIfOptedIn is false, OR
  // 3. The checkbox wasn't initially opted in
  const shouldShowTransactional =
    hasInteractedWithTransactional || !hideIfOptedIn || !hasInitialExplicitTransactionalOptIn;
  const shouldShowMarketing =
    !hideMarketingConsent &&
    (hasInteractedWithMarketing || !hideIfOptedIn || !hasInitialExplicitMarketingOptIn);

  // Script for transactional consent (based on 10DLC requirements)
  const transactionalScript = disclosesAllUseCases
    ? `By providing your phone number, you agree to receive SMS messages from HealthCom on behalf of ${clinicName}, including appointment reminders, account notifications, verification codes (such as MFA/2FA), password reset codes, customer care messages, and health questionnaires and surveys. Message frequency may vary. Standard message and data rates may apply. Reply STOP to opt out. Reply HELP for help. We will not share your mobile information with third parties for promotional or marketing purposes.`
    : hasEHR
      ? `We can send you text messages for appointment reminders and account-related updates. These are not marketing messages. Message frequency may vary. You can opt out anytime by replying STOP, and standard message and data rates may apply. Do you agree to receive these texts?`
      : `By providing your phone number, you agree to receive health questionnaires and surveys from HealthCom on behalf of ${clinicName}. Message frequency may vary. Standard message and data rates may apply. Reply STOP to opt out. Reply HELP for help. We will not share your mobile information with third parties for promotional or marketing purposes.`;

  // Script for marketing consent
  const marketingScript = hasEHR
    ? `Would you like to also receive promotional messages from HealthCom on behalf of ${clinicName}? By providing your number you are consenting to receive health tips, special offers, Google review requests, and clinic updates. Message frequency may vary. Standard message and data rates may apply. You can reply STOP to unsubscribe or HELP for help at any time. We will not share your mobile information with third parties for marketing.`
    : `Would you like to also receive promotional messages from HealthCom on behalf of ${clinicName}? By providing your number you are consenting to receive Google review requests, health tips, and clinic updates. Message frequency may vary. Standard message and data rates may apply. You can reply STOP to unsubscribe or HELP for help at any time. We will not share your mobile information with third parties for marketing.`;

  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
      {!hideHeader && (
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <h4 className="text-sm font-semibold text-gray-900">SMS Messaging Consent</h4>
            </div>
            {!patientView && !transactionalConsent && !marketingConsent && (
              <span
                className={
                  transactionalConsentStatus === SmsConsentStatus.CARRIER_OPTED_OUT ||
                  marketingConsentStatus === SmsConsentStatus.CARRIER_OPTED_OUT
                    ? 'text-xs text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200'
                    : transactionalConsentStatus === SmsConsentStatus.IMPLIED
                      ? 'text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200'
                      : 'text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200'
                }
              >
                {transactionalConsentStatus === SmsConsentStatus.CARRIER_OPTED_OUT ||
                marketingConsentStatus === SmsConsentStatus.CARRIER_OPTED_OUT
                  ? 'Opted out via STOP'
                  : transactionalConsentStatus === SmsConsentStatus.IMPLIED
                    ? 'Implied consent active'
                    : 'No consent'}
              </span>
            )}
          </div>
          {!patientView && (
            <p className="text-xs text-gray-500 mt-1">Manage patient SMS preferences</p>
          )}
        </div>
      )}
      <CarrierOptedOutWarning
        transactionalConsentStatus={transactionalConsentStatus}
        marketingConsentStatus={marketingConsentStatus}
        className="mb-3"
      />
      <div className="space-y-3">
        {shouldShowTransactional && (
          <div>
            <Checkbox
              label={
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {patientView
                      ? disclosesAllUseCases
                        ? `Opt in to reminders, notifications, and other messages`
                        : hasEHR
                          ? `Opt in to reminders and notifications`
                          : `Opt in to health questionnaires`
                      : hasEHR
                        ? 'Appointment reminders'
                        : 'Health questionnaires'}
                  </span>
                  {showConsentScript && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={e => e.stopPropagation()} // Prevent checkbox toggle
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0">
                        <div className="p-3">
                          <div className="text-xs font-semibold text-gray-700 mb-2">
                            Suggested Verbal Consent Script
                          </div>
                          <div className="text-xs text-gray-600 leading-relaxed mb-3">
                            {transactionalScript}
                          </div>
                        </div>
                        <div className="bg-gray-50 px-3 py-2 border-t border-gray-100">
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Use when obtaining verbal consent
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              }
              description={
                <p className="text-xs text-gray-500">
                  {patientView ? (
                    <>
                      By providing your phone number and selecting your preferences, you agree to
                      receive{' '}
                      {disclosesAllUseCases ? (
                        <>
                          SMS messages from {clinicName}, including appointment reminders, account
                          notifications, verification codes (such as MFA/2FA), password reset codes,
                          customer care messages, and health questionnaires and surveys
                        </>
                      ) : (
                        <>
                          {hasEHR
                            ? 'SMS appointment reminders and notifications'
                            : 'health questionnaires and surveys'}{' '}
                          from {clinicName}
                        </>
                      )}
                      . Message frequency may vary. Standard message and data rates may apply. Reply
                      STOP to opt out. Reply HELP for help. Consent is not a condition of care. We
                      will not sell or share your mobile information with third parties for
                      marketing purposes.{' '}
                      <a
                        href={
                          clinicPath
                            ? `/${clinicPath}/privacy-policy`
                            : `/id/${clinicId}/privacy-policy`
                        }
                        target={clinicPath ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        Privacy Policy
                      </a>
                    </>
                  ) : hasEHR ? (
                    'Appointment confirmations, reminders, and care updates'
                  ) : (
                    'Health questionnaires and surveys'
                  )}
                </p>
              }
              value={transactionalConsent}
              onChange={handleTransactionalChange}
              errors={transactionalErrors}
              disabled={
                disabled || transactionalConsentStatus === SmsConsentStatus.CARRIER_OPTED_OUT
              }
            />
          </div>
        )}

        {shouldShowMarketing && (
          <div>
            <Checkbox
              label={
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {patientView
                      ? `Opt in to promotions and marketing`
                      : `Promotions and marketing messages`}
                  </span>
                  {marketingConsentStatus === SmsConsentStatus.PENDING_CONFIRMATION ? (
                    <span className="text-xs font-normal text-amber-600">
                      (pending confirmation)
                    </span>
                  ) : (
                    <span className="text-xs font-normal text-gray-400">(optional)</span>
                  )}
                  {showConsentScript && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={e => e.stopPropagation()} // Prevent checkbox toggle
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0">
                        <div className="p-3">
                          <div className="text-xs font-semibold text-gray-700 mb-2">
                            Marketing Consent Script
                          </div>
                          <div className="text-xs text-gray-600 leading-relaxed mb-3">
                            {marketingScript}
                          </div>
                        </div>
                        <div className="bg-amber-50 px-3 py-2 border-t border-amber-100">
                          <div className="text-xs text-amber-700 flex items-start gap-1">
                            <svg
                              className="w-3 h-3 mt-0.5 shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <span>Patient must reply YES to SMS to activate marketing</span>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              }
              description={
                <div>
                  <p className="text-xs text-gray-500">
                    {patientView ? (
                      <>
                        By providing your phone number and selecting your preferences, you agree to
                        receive SMS promotions and marketing messages from {clinicName}. Message
                        frequency may vary. Standard message and data rates may apply. Reply STOP to
                        opt out. Reply HELP for help. Consent is not a condition of care. We will
                        not sell or share your mobile information with third parties for marketing
                        purposes.{' '}
                        <a
                          href={
                            clinicPath
                              ? `/${clinicPath}/privacy-policy`
                              : `/id/${clinicId}/privacy-policy`
                          }
                          target={clinicPath ? '_self' : '_blank'}
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Privacy Policy
                        </a>
                      </>
                    ) : (
                      'Google review links'
                    )}
                  </p>
                  {marketingConsentStatus === SmsConsentStatus.PENDING_CONFIRMATION &&
                    !patientView && (
                      <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700 border border-amber-100">
                        <strong>Pending:</strong> Waiting for patient to confirm via SMS. Patient
                        must reply YES to the confirmation text.
                      </div>
                    )}
                  {marketingConsent &&
                    marketingConsentStatus !== SmsConsentStatus.PENDING_CONFIRMATION &&
                    marketingConsentStatus !== SmsConsentStatus.OPTED_IN &&
                    !patientView && (
                      <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700 border border-amber-100">
                        <strong>Note:</strong> Patient will receive a confirmation SMS. They must
                        reply YES to activate marketing messages.
                      </div>
                    )}
                </div>
              }
              value={
                marketingConsent || marketingConsentStatus === SmsConsentStatus.PENDING_CONFIRMATION
              }
              onChange={handleMarketingChange}
              errors={marketingErrors}
              disabled={
                disabled ||
                marketingConsentStatus === SmsConsentStatus.CARRIER_OPTED_OUT ||
                marketingConsentStatus === SmsConsentStatus.PENDING_CONFIRMATION
              }
            />
          </div>
        )}

        {patientView && (transactionalConsent || marketingConsent) && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Message and data rates may apply. Reply STOP to unsubscribe.
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Terms
              </a>
              {' · '}
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Privacy
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
